/**
 * /fiches/moto/livret-progression-plateau-a2 — Livret de progression A4 imprimable
 * Chaque compétence : POURQUOI · COMMENT · RISQUE + cases Découvert / En cours / Acquis
 * L'élève coche à son rythme, avec son moniteur.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Printer, Download, ArrowLeft, QrCode, X, Copy, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

export default function FicheLivretProgressionPlateauA2() {
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = 'Livret de progression · Plateau A2 · code2roues';
  }, []);

  const printPage = () => window.print();
  const publicUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/fiches/eleve/livret-plateau-a2`
    : 'https://code2roues.fr/fiches/eleve/livret-plateau-a2';

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* silent */ }
  };

  return (
    <div className="min-h-screen bg-neutral-100 print:bg-white">
      <div className="print:hidden bg-neutral-900 border-b border-white/10 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/moniteur/dashboard" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-[13px] font-semibold">
            <ArrowLeft size={14} /> Retour au BO moniteur
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowQR(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 bg-white/[0.04] hover:bg-white/[0.10] text-white/85 text-[12px] font-semibold transition"
              data-testid="btn-share-qr"
            >
              <QrCode size={13} /> Partager (QR)
            </button>
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); printPage(); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 bg-white/[0.04] hover:bg-white/[0.10] text-white/85 text-[12px] font-semibold transition"
              data-testid="btn-download-pdf"
            >
              <Download size={13} /> Télécharger PDF
            </a>
            <button
              onClick={printPage}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-[12px] font-semibold shadow-md transition"
              data-testid="btn-print-fiche"
            >
              <Printer size={13} /> Imprimer
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-6 print:py-0 print:max-w-none">
        <div
          className="bg-white shadow-lg print:shadow-none mx-auto text-neutral-900"
          style={{ width: '210mm', minHeight: '297mm', padding: '14mm 15mm', fontFamily: 'Figtree, system-ui, sans-serif' }}
          data-testid="fiche-sheet"
        >
          {/* En-tête */}
          <header className="flex items-start justify-between border-b-2 border-violet-600 pb-3 mb-4 gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-[9px] font-black tracking-[0.28em] uppercase text-violet-600">code2roues.fr · Livret d&apos;apprentissage</div>
              <h1 className="text-[22px] font-black text-neutral-900 leading-tight mt-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Livret de progression — Plateau A2
              </h1>
              <div className="text-[11px] text-neutral-600 mt-0.5">Coche les cases <strong>à ton rythme</strong> avec ton moniteur. Chaque élève avance différemment.</div>
            </div>
            <div className="flex flex-col items-center shrink-0">
              <div className="bg-white p-1 border border-neutral-300 rounded">
                <QRCodeSVG value={publicUrl} size={64} level="M" includeMargin={false} bgColor="#ffffff" fgColor="#0a0a0b" />
              </div>
              <div className="text-[7.5px] font-bold uppercase tracking-widest text-neutral-500 text-center mt-1 leading-tight">
                Scan pour<br />garder ce livret
              </div>
            </div>
          </header>

          {/* Identification */}
          <section className="grid grid-cols-3 gap-3 mb-4 text-[11px]">
            <IdField label="Élève (nom prénom)" />
            <IdField label="Début formation" />
            <IdField label="Moniteur / auto-école" />
          </section>

          {/* Mode d'emploi */}
          <section className="mb-4 border-l-4 border-violet-600 bg-violet-50 pl-3 py-2 pr-2 rounded-r">
            <div className="text-[10px] font-black uppercase tracking-widest text-violet-700 mb-0.5">💡 Comment utiliser ce livret</div>
            <div className="text-[10.5px] text-neutral-800 leading-snug">
              Ce livret présente <strong>toutes les compétences à acquérir</strong> pour réussir le plateau A2, du <strong>Niveau 0 (théorie fondamentale : regard, position, mécanique, physique, EPI)</strong> jusqu&apos;à la <strong>maîtrise pour l&apos;examen</strong>. Chaque compétence a un <strong className="text-sky-700">POURQUOI</strong> (à quoi ça sert), un <strong className="text-emerald-700">COMMENT</strong> (la technique) et un <strong className="text-red-700">RISQUE</strong> (l&apos;erreur à éviter). Coche <strong>Découvert</strong> quand tu l&apos;as essayé, <strong>En cours</strong> quand tu la travailles, <strong>Acquis</strong> quand ton moniteur valide.
            </div>
          </section>

          {/* NIVEAU 0 : THÉORIE FONDAMENTALE */}
          <NiveauHeader n="0" title="Théorie fondamentale — Comprendre son 2-roues" description="La théorie à maîtriser AVANT de monter. Sans ça, la pratique est dangereuse." />

          <Competence
            n="0.1"
            title="Le regard — la clé absolue du motard"
            pourquoi="Une moto va TOUJOURS où va le regard (loi psychomotrice). Un regard qui traîne = une moto qui zigzague. C&apos;est LE point qui différencie un motard sûr d&apos;un motard dangereux."
            comment="<strong>Haut</strong> (au-dessus de la ligne d&apos;horizon), <strong>loin</strong> (10-15 secondes en avance), <strong>mobile</strong> (balayer la scène : rétros toutes les 5-10 s, angles morts avant de tourner). En virage : regarder la sortie du virage, jamais l&apos;entrée."
            risque="Fixer la roue avant, la moto qui te précède, ou pire un plot au sol = tu vas droit dedans. En virage : regarder l&apos;extérieur = tu élargis et tu sors de la route."
          />

          <Competence
            n="0.2"
            title="Position de conduite — les 5 points d&apos;appui"
            pourquoi="Une bonne position = équilibre stable + contrôle des commandes précis + moins de fatigue. Le motard fait corps avec sa moto grâce à 5 points d&apos;appui."
            comment="<strong>1. Pieds</strong> sur les repose-pieds (pas les talons). <strong>2. Genoux</strong> serrés au réservoir (le verrouillage bassin-moto). <strong>3. Fessiers</strong> ancrés dans la selle. <strong>4. Mains</strong> relâchées sur les poignées (2 doigts sur l&apos;embrayage et le frein). <strong>5. Regard</strong> haut. Dos droit, épaules basses, coudes semi-fléchis."
            risque="Bras tendus = pas de contrôle du guidon en cas de choc. Genoux ouverts = moto instable dans les manœuvres lentes. Se tenir au guidon = la moto part au moindre à-coup."
          />

          <Competence
            n="0.3"
            title="Anatomie mécanique — connaître sa moto"
            pourquoi="Comprendre ce que fait ta moto quand tu actionnes une commande, c&apos;est anticiper ses réactions. Aucun automatisme durable ne se construit sans cette base."
            comment="<strong>Moteur</strong> (4-temps, cylindrée A2 = 35 kW max). <strong>Transmission</strong> : embrayage → boîte 5/6 vitesses → chaîne → roue arrière. <strong>Freins</strong> : disque avant (≈70% du freinage) + tambour ou disque arrière (≈30%). <strong>Direction</strong> : guidon → té de fourche → roue avant. <strong>Suspensions</strong> : fourche avant + amortisseur arrière. <strong>Pneus</strong> : seul lien avec le sol (2 timbres-poste !)."
            risque="Ignorer un bruit anormal, une chaîne détendue, une pression de pneu basse = casse mécanique en pleine leçon ou chute. Faire une VVR à chaque prise en main."
          />

          <Competence
            n="0.4"
            title="Les 4 lois physiques essentielles"
            pourquoi="La moto obéit à des lois physiques que tu ne peux pas ignorer. Les connaître te donne des super-pouvoirs (contre-braquage) et t&apos;évite des accidents (transfert de charge)."
            comment="<strong>1. Adhérence</strong> : 2 timbres-poste au sol, donc adhérence limitée. Elle se partage entre virage/freinage/accélération (règle 100%). <strong>2. Transfert de charge</strong> : au freinage, le poids bascule sur l&apos;avant (fourche qui plonge). Freiner en virage = perte d&apos;adhérence. <strong>3. Contre-braquage</strong> : au-dessus de 20 km/h, on pousse le guidon <strong>du côté du virage</strong> (pousser à gauche = tourner à gauche). Contre-intuitif mais fondamental. <strong>4. Gyroscopie</strong> : les roues qui tournent créent une force de stabilisation. Plus tu vas vite, plus la moto tient droite."
            risque="Freiner brutalement en virage = chute par perte d&apos;adhérence. Vouloir tourner en tirant le guidon à haute vitesse = la moto va tout droit. Aller trop lentement = la moto tombe (perte de gyroscopie)."
          />

          <Competence
            n="0.5"
            title="EPI & sécurité passive"
            pourquoi="En moto, pas d&apos;habitacle. Ton corps est le seul amortisseur. Les EPI (Équipements de Protection Individuelle) réduisent de 70% le risque de blessure grave."
            comment="<strong>Casque</strong> homologué E22, jugulaire bouclée serrée. <strong>Blouson</strong> moto avec protections épaules/coudes/dos (norme CE). <strong>Gants</strong> homologués CE (obligatoires depuis 2016). <strong>Pantalon</strong> moto (jean renforcé kevlar acceptable en formation). <strong>Chaussures</strong> montantes couvrant les malléoles. Toujours en état, jamais prêtés à un autre gabarit."
            risque="Casque non bouclé = envol au premier choc. Baskets = fractures malléoles quasi-garanties en cas de chute. Gants trop grands = perte de sensation au guidon."
          />

          {/* Page break */}
          <div className="print:break-before-page hidden print:block" />

          {/* NIVEAU 1 : FONDAMENTAUX */}
          <NiveauHeader n="1" title="Fondamentaux &mdash; Prise en main" description="Les bases indispensables avant d&apos;aborder les 6 manœuvres officielles." />

          <Competence
            n="1.1"
            title="Équilibre & manipulation à l&apos;arrêt"
            pourquoi="Une moto pese 180-200 kg. Savoir la tenir, la relever et la manipuler sans forcer est la base de tout."
            comment="Corps collé au réservoir, dos droit, utilise ton bassin (pas tes bras). Béquille latérale toujours dans le sens de la pente."
            risque="Chute lors du rebéquillage → vérifier que la béquille est bien déployée avant de descendre."
          />

          <Competence
            n="1.2"
            title="Les 4 commandes moto"
            pourquoi="Maîtriser embrayage (main gauche), accélérateur (main droite), selécteur (pied gauche), freins (avant droit / arrière pied droit) sans hesiter."
            comment="Repérage à l&apos;arrêt, moteur éteint. Répéter chaque geste 10 fois. Ne jamais confondre embrayage et frein avant."
            risque="Confondre frein avant et embrayage → chute immédiate. Règle : main <strong>gauche</strong> = embrayage, main <strong>droite</strong> = frein."
          />

          <Competence
            n="1.3"
            title="Démarrage & arrêt moteur"
            pourquoi="Démarrer et éteindre sans caler ni faire hurler le moteur."
            comment="Neutre → contact → coupe-circuit ON → démarreur. Pour arrêter : passer au neutre puis couper."
            risque="Démarrer en 1ʳᵉ sans embrayage = bond de la moto. Toujours vérifier le neutre avant de démarrer."
          />

          <Competence
            n="1.4"
            title="Point de patinage embrayage"
            pourquoi="Trouver le point où la moto commence à avancer. C&apos;est la clé de tous les démarrages en douceur."
            comment="Moto en butee sur freins avant. Embrayer, puis relacher très lentement en écoutant le moteur qui plonge légèrement."
            risque="Relâcher trop vite → calage répété. Relâcher trop lentement → usure prématurée de l&apos;embrayage."
          />

          <Competence
            n="1.5"
            title="Roulage 1ʳᵉ ligne droite"
            pourquoi="Avancer seul(e), en équilibre, en tenant la moto droite. Première vraie sensation de conduite."
            comment="Regard loin (10-15 m devant), pieds sur les repose-pieds, patinage embrayage doux, légère accélération."
            risque="Fixer la roue avant → la moto zigzague. Règle absolue : <strong>la moto va où va le regard</strong>."
          />

          {/* Page break */}
          <div className="print:break-before-page hidden print:block" />

          {/* NIVEAU 2 : LES 6 MANŒUVRES */}
          <NiveauHeader n="2" title="Les 6 manœuvres officielles du plateau" description="Manœuvres présentes le jour de l&apos;épreuve (source : securite-routiere.gouv.fr)." />

          <Competence
            n="2.1"
            title="M1 — Sans l&apos;aide du moteur"
            pourquoi="Démontrer que tu maîtrises le poids de la moto, moteur éteint. Note A ou B, non éliminatoire."
            comment="Débéquiller en A → pousser jusqu&apos;à B → s&apos;arrêter → reculer → rebéquiller en A. En marchant à côté, pas assis(e)."
            risque="Faire tomber la moto en la reculant. Toujours garder le corps côté haut de la pente."
          />

          <Competence
            n="2.2"
            title="M2 — Allure réduite sans passager (chrono 16 s)"
            pourquoi="Prouver que tu maîtrises la moto à basse vitesse dans un slalom. Note A si chrono ≥ 16 s entre A et B."
            comment="Regard loin (jamais sur les plots), patinage embrayage constant, frein arrière en régulation, genoux serrés au réservoir."
            risque="Fixer les plots → tu les touches. Chrono &lt; 14 s = éliminatoire."
          />

          <Competence
            n="2.3"
            title="M3 — Freinage d&apos;urgence (≥ 50 km/h)"
            pourquoi="Prouver que tu sais t&apos;arrêter vite et précisément en cas d&apos;urgence. Radar au C6."
            comment="Accélérer franchement jusqu&apos;à 50 km/h min. Relacher les gaz → frein avant progressif (70%) + frein arrière (30%). Arrêt avant C5 (sec) / C4 (mouillé)."
            risque="Bloquer la roue avant → chute. Freinage avant C6 = éliminatoire. Vitesse insuffisante = éliminatoire."
          />

          <Competence
            n="2.4"
            title="M4 — Allure réduite AVEC passager"
            pourquoi="Démontrer que tu peux rouler en sécurité avec un passager (gestion du poids, communication)."
            comment="Passager annoncé (fauteuil arrière). Partir doucement, boucle lente, arrêt précis pour déposer. Équilibre = plus difficile."
            risque="Démarrer brusquement avec un passager = équilibre perdu. Décalage du centre de gravité vers l&apos;arrière."
          />

          <Competence
            n="2.5"
            title="M5 — Slalom (≥ 40 km/h C7, ≥ 50 km/h C6)"
            pourquoi="Maîtriser la moto en enchainement rapide de virages serrés + accélération franche après demi-tour."
            comment="Regard sur le plot suivant (pas celui d&apos;avant). Contre-braquage : appuyer sur le guidon <strong>du côté du virage</strong>. Genoux au réservoir."
            risque="Freiner dans le slalom → chute. Regarder les plots → les toucher. Vitesse insuffisante = éliminatoire."
          />

          <Competence
                n="2.6"
            title="M6 — Évitement (50 km/h + freinage plots colorés)"
            pourquoi="Simuler l&apos;évitement d&apos;un obstacle soudain à vitesse route + arrêt précis."
            comment="Arriver à 50 km/h franchi. Contre-braquer sec pour éviter l&apos;obstacle. Remétre la moto droite. Freinage précis entre les plots."
            risque="Se raidir sur les bras → la moto ne bascule pas. Évitement trop tardif → heurt de l&apos;obstacle."
          />

          {/* Page break */}
          <div className="print:break-before-page hidden print:block" />

          {/* NIVEAU 3 : MAÎTRISE EXAMEN */}
          <NiveauHeader n="3" title="Maîtrise pour l&apos;examen" description="Enchaîner les 6 manœuvres dans les 10 min et gérer le stress le jour J." />

          <Competence
            n="3.1"
            title="Enchaînement des 6 manœuvres"
            pourquoi="L&apos;épreuve dure 10 min. Il faut enchaîner sans temps mort ni hésitation."
            comment="Répétition du parcours complet plusieurs fois. Visualisation mentale entre les passages."
            risque="Trou de mémoire au milieu du parcours = échec. Anticiper la suite à chaque instant."
          />

          <Competence
            n="3.2"
            title="Gestion du stress"
            pourquoi="Le jour J, le stress fait perdre 30% de tes moyens. Il faut le désamorcer."
            comment="Respirer profondément avant le départ. Se concentrer sur SES gestes, pas sur l&apos;examinateur. Se dire « j&apos;ai réussi à l&apos;entraînement, je peux le refaire »."
            risque="Blocage total à la 1ʳᵉ manœuvre → chute d&apos;attention pour les suivantes."
          />

          <Competence
            n="3.3"
            title="Examens blancs"
            pourquoi="Se mettre en conditions réelles pour identifier tes derniers points faibles."
            comment="Simulation minimum 3 fois avec un moniteur qui joue l&apos;examinateur (chronomètre, silence, notation)."
            risque="Passer l&apos;examen sans avoir simulé = surprise le jour J. Toujours faire au moins 2 examens blancs."
          />

          {/* Validation finale */}
          <section className="mt-4 border-2 border-emerald-500 rounded-lg p-3 bg-emerald-50">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-1">🎯 Validation finale par le moniteur</div>
            <div className="text-[10.5px] text-emerald-900 leading-snug mb-2">
              Je certifie que l&apos;élève maîtrise l&apos;ensemble des compétences ci-dessus et est <strong>prêt(e) à se présenter</strong> à l&apos;épreuve plateau A2.
            </div>
            <div className="grid grid-cols-3 gap-3 mt-2">
              <IdField label="Date de validation" />
              <IdField label="Signature moniteur" />
              <IdField label="Cachet auto-école" />
            </div>
          </section>

          {/* Pied */}
          <footer className="mt-4 pt-3 border-t border-neutral-200 flex items-center justify-between text-[9px] text-neutral-500">
            <div>code2roues.fr · Livret de progression Plateau A2 · v1.0</div>
            <div>À conserver avec ton livret d&apos;apprentissage</div>
          </footer>
        </div>
      </div>

      {/* Modale QR */}
      {showQR && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 print:hidden"
          onClick={() => setShowQR(false)}
          data-testid="modal-qr-share"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-violet-600 text-white px-5 py-3 flex items-center justify-between">
              <div>
                <div className="text-[9px] font-black uppercase tracking-[0.22em] text-white/70">Partage rapide</div>
                <div className="text-[14px] font-bold">Livret de progression Plateau A2</div>
              </div>
              <button onClick={() => setShowQR(false)} className="text-white/80 hover:text-white p-1" data-testid="btn-close-qr" aria-label="Fermer">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 flex flex-col items-center">
              <div className="bg-white p-3 rounded-lg border-2 border-neutral-200">
                <QRCodeSVG value={publicUrl} size={220} level="H" includeMargin={false} bgColor="#ffffff" fgColor="#0a0a0b" />
              </div>
              <div className="mt-4 text-center">
                <div className="text-[11px] text-neutral-500 uppercase tracking-widest font-bold mb-1">Scannez avec un smartphone</div>
                <div className="text-[13px] text-neutral-800">L&apos;élève consulte son livret à tout moment</div>
              </div>
              <div className="mt-4 w-full flex items-center gap-2 border border-neutral-300 rounded-lg px-3 py-2 bg-neutral-50">
                <div className="flex-1 text-[11px] text-neutral-700 truncate font-mono" data-testid="qr-url">{publicUrl}</div>
                <button
                  onClick={copyUrl}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold transition ${
                    copied ? 'bg-emerald-600 text-white' : 'bg-neutral-900 text-white hover:bg-neutral-700'
                  }`}
                  data-testid="btn-copy-url"
                >
                  {copied ? <><Check size={11} /> Copié</> : <><Copy size={11} /> Copier</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          html, body { background: #fff !important; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}

// ─── Sous-composants ───────────────────────────────────────────────

function IdField({ label }) {
  return (
    <div className="border border-neutral-300 rounded p-1.5 h-[42px] flex flex-col justify-between">
      <div className="text-[8.5px] font-bold uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="border-b border-dotted border-neutral-400 h-3" />
    </div>
  );
}

function NiveauHeader({ n, title, description }) {
  return (
    <div className="flex items-center gap-2 mt-3 mb-2">
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-violet-600 text-white text-[12px] font-black shrink-0">
        {n}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-black text-neutral-900 leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Niveau {n} — {title}
        </div>
        <div className="text-[9.5px] text-neutral-600 italic leading-tight">{description}</div>
      </div>
    </div>
  );
}

function Competence({ n, title, pourquoi, comment, risque }) {
  return (
    <div className="border border-neutral-300 rounded-lg p-2 mb-2 bg-white avoid-page-break">
      <div className="flex items-start gap-2 mb-1.5">
        <span className="inline-flex items-center justify-center px-1.5 h-5 rounded bg-violet-100 text-violet-700 text-[10px] font-black shrink-0">
          {n}
        </span>
        <div className="text-[11.5px] font-black text-neutral-900 leading-tight flex-1">{title}</div>
      </div>

      <div className="grid grid-cols-3 gap-1.5 mb-1.5">
        <BlocPCR label="Pourquoi" color="#0284c7" bg="#f0f9ff" text={pourquoi} />
        <BlocPCR label="Comment" color="#059669" bg="#ecfdf5" text={comment} />
        <BlocPCR label="Risque" color="#dc2626" bg="#fef2f2" text={risque} />
      </div>

      {/* Cases de validation */}
      <div className="flex items-center gap-2 pt-1.5 border-t border-neutral-200 text-[9.5px]">
        <ValidCheck label="Découvert" color="#94a3b8" />
        <ValidCheck label="En cours" color="#f59e0b" />
        <ValidCheck label="Acquis" color="#10b981" bold />
        <div className="ml-auto flex items-center gap-1 text-neutral-500">
          <span className="font-bold">Date :</span>
          <span className="inline-block border-b border-dotted border-neutral-400" style={{ width: 60 }} />
          <span className="font-bold ml-1">Visa mon. :</span>
          <span className="inline-block border-b border-dotted border-neutral-400" style={{ width: 40 }} />
        </div>
      </div>
    </div>
  );
}

function BlocPCR({ label, color, bg, text }) {
  // Support of &lt;strong&gt; in text (rendered as <strong>) via dangerouslySetInnerHTML
  return (
    <div className="rounded p-1.5" style={{ background: bg, border: `1px solid ${color}30` }}>
      <div className="text-[8px] font-black uppercase tracking-widest mb-0.5" style={{ color }}>
        {label}
      </div>
      <div className="text-[9.5px] text-neutral-800 leading-snug" dangerouslySetInnerHTML={{ __html: text }} />
    </div>
  );
}

function ValidCheck({ label, color, bold }) {
  return (
    <div className="flex items-center gap-1">
      <span className="inline-block w-[12px] h-[12px] rounded-[2px] border-2 shrink-0" style={{ borderColor: color }} />
      <span className={`text-neutral-700 ${bold ? 'font-black' : 'font-semibold'}`} style={bold ? { color } : {}}>
        {label}
      </span>
    </div>
  );
}
